/*
 * GA4 web error monitoring — the web analog of Crashlytics (no Crashlytics web SDK exists).
 * Emits GA4 `exception` events for uncaught errors and unhandled promise rejections.
 *
 * gtag.js itself is bootstrapped inline in layout/theme.liquid, gated on the
 * `ga4_measurement_id` theme setting. This file only wires the error handlers and
 * no-ops gracefully when gtag has not loaded (no measurement ID configured), so it
 * is always safe to include.
 *
 * Card OjwMedyn — mandatory observability baseline.
 *
 * Card WrpTHgyJ — make the next exception TRIAGEABLE from GA4 alone. 162 exception
 * events in 90 days (108 on the home page, from ~10 users at ~11 events each) were
 * unreadable: `description` was the only fact and it was not a registered dimension,
 * so nothing but the count and the page ever came back out. Two changes:
 *   1. Every event now carries STRUCTURED params next to `description` —
 *      `error_kind` (error | rejection | manual), `error_name` (the constructor
 *      name), `error_source` (top frame as path:line:col) and `repeat_count`.
 *      All four are registered as event-scoped custom dimensions on the Studio
 *      property (543894348) together with `description` and `fatal`, so a
 *      `runReport` on `customEvent:error_source` names the failing script
 *      without a browser session.
 *   2. Identical descriptions are capped at MAX_REPEATS_PER_LOAD per page load.
 *      The ~11-per-user pattern is one fault re-firing (a loop, an observer, a
 *      retrying fetch); resending it dozens of times inflates the count without
 *      adding a fact, and it is exactly what hid the DISTINCT faults behind it.
 *      The cap is per description, so a second, different error still reports.
 */
(function () {
  'use strict';

  var MAX_REPEATS_PER_LOAD = 5;
  var DESCRIPTION_MAX = 500;
  var SOURCE_MAX = 100;
  var seen = Object.create(null);

  function clip(value, max) {
    return String(value == null ? '' : value).slice(0, max);
  }

  // "path:line:col" of the first stack frame that names a script. Browsers
  // disagree on frame syntax (V8 `at fn (url:l:c)`, JSC/Gecko `fn@url:l:c`), so
  // this only looks for a URL followed by :line:col and takes the FIRST one.
  function topFrame(stack) {
    if (!stack) return '';
    var m = /((?:https?:)?\/\/[^\s()]+?):(\d+):(\d+)/.exec(String(stack));
    if (!m) return '';
    var path = m[1].replace(/^(?:https?:)?\/\/[^/]+/, '').replace(/\?.*$/, '');
    return clip((path || m[1]) + ':' + m[2] + ':' + m[3], SOURCE_MAX);
  }

  function sourceOf(filename, lineno, colno) {
    if (!filename) return '';
    var path = String(filename).replace(/^(?:https?:)?\/\/[^/]+/, '').replace(/\?.*$/, '');
    return clip((path || filename) + ':' + (lineno || 0) + ':' + (colno || 0), SOURCE_MAX);
  }

  function nameOf(err, fallback) {
    if (err && typeof err.name === 'string' && err.name) return clip(err.name, 60);
    if (err && err.constructor && typeof err.constructor.name === 'string' && err.constructor.name) {
      return clip(err.constructor.name, 60);
    }
    return fallback;
  }

  function send(kind, description, fatal, name, source) {
    try {
      if (typeof window.gtag !== 'function') return;
      var key = clip(description || 'unknown error', DESCRIPTION_MAX);
      var count = (seen[key] || 0) + 1;
      seen[key] = count;
      if (count > MAX_REPEATS_PER_LOAD) return;
      window.gtag('event', 'exception', {
        description: key,
        fatal: !!fatal,
        error_kind: kind,
        error_name: name || 'unknown',
        error_source: source || '',
        repeat_count: count,
      });
    } catch (e) {
      // Swallow — error reporting must never itself throw.
    }
  }

  // Uncaught runtime errors.
  window.addEventListener('error', function (event) {
    var msg;
    var err = event && event.error;
    if (err && err.stack) {
      msg = err.stack;
    } else if (event && event.message) {
      msg = event.message +
        (event.filename ? ' @ ' + event.filename + ':' + event.lineno + ':' + event.colno : '');
    } else {
      msg = 'window.onerror (no detail)';
    }
    var source = (event && sourceOf(event.filename, event.lineno, event.colno)) || topFrame(err && err.stack);
    send('error', msg, false, nameOf(err, err ? 'unknown' : 'message-only'), source);
  });

  // Unhandled promise rejections.
  window.addEventListener('unhandledrejection', function (event) {
    var reason = event && event.reason;
    var msg;
    if (reason && reason.stack) {
      msg = reason.stack;
    } else if (reason) {
      // JSON.stringify throws on circular references (common in error/network/DOM
      // objects); guard it so the error handler never throws itself (Gemini PR#49).
      var reasonStr;
      try {
        reasonStr = typeof reason === 'object' ? JSON.stringify(reason) : String(reason);
      } catch (e) {
        reasonStr = '[Unserializable Object]';
      }
      msg = 'Unhandled rejection: ' + reasonStr;
    } else {
      msg = 'Unhandled promise rejection (no reason)';
    }
    var name = reason && typeof reason === 'object' ? nameOf(reason, 'rejection') : 'rejection';
    send('rejection', msg, false, name, topFrame(reason && reason.stack));
  });

  // Expose a manual hook for app code / QA to force a tracked exception.
  // Used by the DebugView verification step in card OjwMedyn. Signature unchanged.
  window.ga4TrackException = function (description, fatal) {
    send('manual', description, fatal, 'manual', '');
  };
})();
